import React, { useEffect, useState } from "react";
import { Download, Share, SquarePlus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true));

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS reports as Mac
};

// Persistent "Install app" button — always available (not a transient banner). Uses the globally
// captured beforeinstallprompt on Android/desktop Chrome; on iOS (no prompt API) it opens a
// step-by-step "Add to Home Screen" guide, since Apple only allows manual installation.
export function InstallButton() {
  const { t } = useI18n();
  const [available, setAvailable] = useState(!!(typeof window !== "undefined" && window.__esvanInstallPrompt));
  const [installed, setInstalled] = useState(isStandalone());
  const [iosGuide, setIosGuide] = useState(false);

  useEffect(() => {
    const onAvail = () => setAvailable(true);
    const onInstalled = () => { setInstalled(true); setAvailable(false); };
    window.addEventListener("esvan-installable", onAvail);
    window.addEventListener("esvan-installed", onInstalled);
    return () => {
      window.removeEventListener("esvan-installable", onAvail);
      window.removeEventListener("esvan-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    const p = window.__esvanInstallPrompt;
    if (p) {
      p.prompt();
      try { await p.userChoice; } catch (_) { /* ignore */ }
      window.__esvanInstallPrompt = null;
      setAvailable(false);
    } else if (isIOS()) {
      setIosGuide(true);
    } else {
      toast(t("pwa.installHint"), { duration: 7000 });
    }
  };

  const Step = ({ n, icon: Icon, text }) => (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2D5A4C] text-xs font-bold text-white">{n}</span>
      <span className="flex flex-1 items-center gap-2 text-sm text-[#1A2E26]" dir="auto">
        {Icon && <Icon size={18} className="shrink-0 text-[#2D5A4C]" />}
        <span>{text}</span>
      </span>
    </li>
  );

  return (
    <>
      <button
        data-testid="install-app-button"
        onClick={onClick}
        className="flex shrink-0 items-center gap-1 rounded-full border border-[#C4DFD2] bg-[#EDF7F2] px-2.5 py-1.5 text-[#2D5A4C] transition-colors hover:bg-[#E1F0E8] active:scale-95"
        aria-label={t("pwa.installApp")}
        title={t("pwa.installApp")}
      >
        <Download size={16} />
        <span className="text-xs font-bold">{t("pwa.installApp")}</span>
      </button>

      <Dialog open={iosGuide} onOpenChange={setIosGuide}>
        <DialogContent data-testid="ios-install-guide" className="max-w-[340px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1A2E26]">{t("pwa.iosTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("pwa.installApp")}</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 py-1">
            <Step n={1} icon={Share} text={t("pwa.iosStep1")} />
            <Step n={2} icon={SquarePlus} text={t("pwa.iosStep2")} />
            <Step n={3} icon={Plus} text={t("pwa.iosStep3")} />
          </ol>
          <button
            data-testid="ios-install-gotit"
            onClick={() => setIosGuide(false)}
            className="mt-1 w-full rounded-xl bg-[#2D5A4C] py-2.5 text-sm font-semibold text-white active:scale-[0.99]"
          >
            {t("pwa.gotIt")}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
